import { api } from '../lib/api'

export type BroadcastRequestPayload = {
  title?: string
  summary: string
  details_md?: string
  client_location_text: string
  client_zipcode: string
  urgency?: 'FLEXIBLE' | 'TODAY' | 'ASAP'
  scheduled_for?: string | null
  attachments?: unknown[]
  skills?: string[] | string
  radius_miles?: number
}

export type BroadcastRequestResponse = {
  job_id: number
  post_id: number
  url: string
  matched_count: number
  delivery_ttl_hours?: number
}

export type ProRequestInboxItem = {
  id: string
  type: 'delivery' | 'invite'
  status: 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED'
  expires_at?: string | null
  expired_at?: string | null
  created_at: string
  updated_at: string
  amount_pixe_hint?: number | null
  deposit_percent?: number | null
  source?: 'invite' | 'discovery' | 'direct' | 'returning_client'
  fee_pct?: string | number | null
  net_pixe_hint?: number | null
  expiring_soon?: boolean
  job: {
    id: number
    post_id?: number | null
    title?: string
    summary?: string
    job_mode?: string
    compensation_mode?: 'HOURLY' | 'FIXED' | 'SALARY' | string
    proposed_hourly_pixe?: number | null
    proposed_fixed_price_pixe?: number | null
    min_rate_cents?: number | null
    max_rate_cents?: number | null
    fixed_price_cents?: number | null
    category?: string | null
    subcategory?: string | null
    escrow_status?: 'NONE' | 'HELD' | 'RELEASED' | 'REFUNDED' | string
    scheduled_for?: string | null
    thread_id?: number | null
    urgency?: string | null
    client_location_text?: string
    client_zipcode?: string
    invitation_status?: string | null
    client?: {
      id: number
      handle?: string
      display_name?: string
      avatar_url?: string
    }
  }
}

export type ProRequestInboxSummary = {
  pending_count: number
  claimed_count: number
  declined_count: number
  expired_count: number
  expiring_soon_count: number
  total_pixe_hint_pending: number
  total_net_pixe_hint_pending: number
  oldest_pending_age_hours: number
}

export type ProRequestDetail = {
  id: string
  type: 'delivery' | 'invite'
  status: 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED'
  created_at: string
  requested_by: {
    handle?: string
    display_name?: string
    avatar_url?: string
  }
  job: {
    id: number
    title?: string
    description?: string
    category?: string | null
    location?: string | null
    mode?: string | null
    budget_hint?: string | null
    created_at?: string
  }
  messages: Array<{
    id: string
    author: string
    body: string
    created_at: string
  }>
  quote_draft: null | {
    id: number
    amount_pixe: number | null
    description: string
    status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'EXPIRED' | 'CANCELED'
    currency: string
    sent_at?: string | null
    accepted_at?: string | null
    updated_at: string
  }
}

export type ProQuoteDraftUpsertPayload = {
  amount_pixe?: number | null
  description?: string
}

let inboxEndpointsAvailable = true

export async function createBroadcastRequest(payload: BroadcastRequestPayload): Promise<BroadcastRequestResponse> {
  const { data } = await api.post<BroadcastRequestResponse>('/api/pros/requests/', payload)
  return data
}

export async function getInbox(
  page = 1,
  pageSize = 20,
  status?: 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED',
  opts?: { q?: string; sort?: 'newest' | 'expiring' | 'highest_value' | 'highest_net' },
): Promise<{ count: number; page: number; page_size: number; status?: string; results: ProRequestInboxItem[] }> {
  if (!inboxEndpointsAvailable) {
    return { count: 0, page, page_size: pageSize, status, results: [] }
  }
  const params: Record<string, string | number> = { page, page_size: pageSize }
  if (status) params.status = status
  if (opts?.q) params.q = opts.q
  if (opts?.sort) params.sort = opts.sort
  const { data } = await api.get('/api/pros/requests/inbox/', { params })
  return data
}

export async function getInboxCount(): Promise<{ pending_count: number }> {
  if (!inboxEndpointsAvailable) return { pending_count: 0 }
  try {
    const { data } = await api.get<{ pending_count: number }>('/api/pros/requests/inbox/count/')
    return data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      inboxEndpointsAvailable = false
      return { pending_count: 0 }
    }
    throw error
  }
}

export async function getInboxSummary(range = 30): Promise<ProRequestInboxSummary> {
  const { data } = await api.get<ProRequestInboxSummary>('/api/pros/requests/summary/', { params: { range } })
  return data
}

export async function getProsRequestDetail(requestId: string): Promise<ProRequestDetail> {
  const { data } = await api.get<ProRequestDetail>(`/api/pros/requests/${encodeURIComponent(requestId)}/`)
  return data
}

export async function acceptProsRequest(requestId: string): Promise<ProRequestDetail> {
  const { data } = await api.post<ProRequestDetail>(`/api/pros/requests/${encodeURIComponent(requestId)}/accept/`, {})
  return data
}

export async function declineProsRequest(requestId: string): Promise<ProRequestDetail> {
  const { data } = await api.post<ProRequestDetail>(`/api/pros/requests/${encodeURIComponent(requestId)}/decline/`, {})
  return data
}

export async function upsertProsQuoteDraft(requestId: string, payload: ProQuoteDraftUpsertPayload) {
  const { data } = await api.post(`/api/pros/requests/${encodeURIComponent(requestId)}/quote/`, payload)
  return data
}

export async function sendProsQuoteDraft(requestId: string): Promise<{ quote: ProRequestDetail['quote_draft']; job_id: number; status: string }> {
  const { data } = await api.post(`/api/pros/requests/${encodeURIComponent(requestId)}/quote/send/`, {})
  return data
}
